import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Campaign as AnnouncementIcon,
  NewReleases as NewFeatureIcon,
  LocalOffer as PromoIcon,
  Palette as ThemesIcon,
} from '@mui/icons-material';
import { notificationsApi } from '../../api';
import type { Notification, NotificationType, CreateSystemNotificationInput, UpdateSystemNotificationInput } from '../../api';
import { useDocumentTitle } from '../../hooks/core';

type SystemNotificationType = Extract<
  NotificationType,
  'system_promotion' | 'system_new_feature' | 'system_new_themes' | 'system_announcement'
>;

const NOTIFICATION_TYPE_CONFIG: Record<SystemNotificationType, { label: string; icon: React.ReactNode; color: string }> = {
  system_announcement: { label: 'Announcement', icon: <AnnouncementIcon />, color: '#1976d2' },
  system_new_feature: { label: 'New Feature', icon: <NewFeatureIcon />, color: '#2e7d32' },
  system_promotion: { label: 'Promotion', icon: <PromoIcon />, color: '#ed6c02' },
  system_new_themes: { label: 'New Themes', icon: <ThemesIcon />, color: '#9c27b0' },
};

export function AdminNotificationsPage() {
  useDocumentTitle('System Notifications');

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create notification dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newNotification, setNewNotification] = useState<CreateSystemNotificationInput>({
    type: 'system_announcement',
    title: '',
    message: '',
  });

  // Edit notification dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editNotification, setEditNotification] = useState<Notification | null>(null);
  const [editInput, setEditInput] = useState<UpdateSystemNotificationInput>({});

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<Notification | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await notificationsApi.adminList({ limit: 100 });
      setNotifications(data.notifications);
      setError(null);
    } catch (err) {
      setError('Failed to load notifications');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    try {
      setCreateLoading(true);
      setCreateError(null);
      await notificationsApi.adminCreate(newNotification);
      setSuccess('Notification created and sent to all users');
      setCreateDialogOpen(false);
      resetCreateForm();
      fetchData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create notification';
      setCreateError(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editNotification) return;
    try {
      setEditLoading(true);
      setEditError(null);
      await notificationsApi.adminUpdate(editNotification.id, editInput);
      setSuccess('Notification updated');
      setEditDialogOpen(false);
      fetchData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update notification';
      setEditError(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  const openDeleteDialog = (notification: Notification) => {
    setNotificationToDelete(notification);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!notificationToDelete) return;
    try {
      setDeleteLoading(true);
      await notificationsApi.adminDelete(notificationToDelete.id);
      setSuccess('Notification deleted');
      setDeleteDialogOpen(false);
      setNotificationToDelete(null);
      fetchData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete notification';
      setError(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetCreateForm = () => {
    setNewNotification({
      type: 'system_announcement',
      title: '',
      message: '',
    });
    setCreateError(null);
  };

  const openEditDialog = (notification: Notification) => {
    setEditNotification(notification);
    setEditInput({
      title: notification.title,
      message: notification.message,
      actionUrl: notification.actionUrl,
      actionLabel: notification.actionLabel,
      expiresAt: notification.expiresAt,
    });
    setEditError(null);
    setEditDialogOpen(true);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isExpired = (notification: Notification): boolean => {
    if (!notification.expiresAt) return false;
    return new Date(notification.expiresAt) < new Date();
  };

  const getTypeConfig = (type: NotificationType) => {
    return NOTIFICATION_TYPE_CONFIG[type as SystemNotificationType] || {
      label: type,
      icon: <NotificationsIcon />,
      color: '#757575',
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <NotificationsIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">System Notifications</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Notification
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Create system-wide notifications to announce promotions, new features, themes, or important updates.
        Notifications are delivered in real-time to all connected users.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Notification</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No system notifications found. Create one to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                notifications.map((notification) => {
                  const typeConfig = getTypeConfig(notification.type);
                  const expired = isExpired(notification);
                  return (
                    <TableRow key={notification.id} hover sx={{ opacity: expired ? 0.6 : 1 }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Chip
                            icon={typeConfig.icon as React.ReactElement}
                            label={typeConfig.label}
                            size="small"
                            sx={{
                              bgcolor: typeConfig.color,
                              color: 'white',
                              '& .MuiChip-icon': { color: 'white' },
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography fontWeight="medium">
                                {notification.title}
                              </Typography>
                              {expired && (
                                <Chip label="Expired" size="small" color="warning" sx={{ height: 20 }} />
                              )}
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {notification.message}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {formatDate(notification.createdAt)}
                        </Typography>
                        {notification.expiresAt && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Expires: {formatDate(notification.expiresAt)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditDialog(notification)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => openDeleteDialog(notification)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Notification Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create System Notification</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {createError && (
              <Alert severity="error" onClose={() => setCreateError(null)}>
                {createError}
              </Alert>
            )}

            <FormControl fullWidth>
              <InputLabel>Notification Type</InputLabel>
              <Select
                value={newNotification.type}
                label="Notification Type"
                onChange={(e) => setNewNotification({ ...newNotification, type: e.target.value as NotificationType })}
              >
                {Object.entries(NOTIFICATION_TYPE_CONFIG).map(([type, config]) => (
                  <MenuItem key={type} value={type}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {config.icon}
                      {config.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Title"
              value={newNotification.title}
              onChange={(e) => setNewNotification({ ...newNotification, title: e.target.value })}
              fullWidth
              required
              helperText="A short, attention-grabbing title"
            />

            <TextField
              label="Message"
              value={newNotification.message}
              onChange={(e) => setNewNotification({ ...newNotification, message: e.target.value })}
              fullWidth
              required
              multiline
              rows={3}
              helperText="The main notification content"
            />

            <TextField
              label="Action URL"
              value={newNotification.actionUrl || ''}
              onChange={(e) => setNewNotification({ ...newNotification, actionUrl: e.target.value || undefined })}
              fullWidth
              helperText="Optional: URL to navigate to when clicked (e.g., /themes, /credits)"
            />

            <TextField
              label="Action Button Label"
              value={newNotification.actionLabel || ''}
              onChange={(e) => setNewNotification({ ...newNotification, actionLabel: e.target.value || undefined })}
              fullWidth
              helperText="Optional: Label for the action button (e.g., 'View Themes', 'Learn More')"
            />

            {newNotification.type === 'system_promotion' && (
              <TextField
                label="Coupon Code"
                value={newNotification.couponCode || ''}
                onChange={(e) => setNewNotification({ ...newNotification, couponCode: e.target.value || undefined })}
                fullWidth
                helperText="Optional: Include a coupon code for promotions"
              />
            )}

            {newNotification.type === 'system_new_feature' && (
              <TextField
                label="Feature Name"
                value={newNotification.featureName || ''}
                onChange={(e) => setNewNotification({ ...newNotification, featureName: e.target.value || undefined })}
                fullWidth
                helperText="Optional: Name of the new feature"
              />
            )}

            <TextField
              label="Expires At"
              type="datetime-local"
              value={newNotification.expiresAt ? newNotification.expiresAt.slice(0, 16) : ''}
              onChange={(e) => setNewNotification({
                ...newNotification,
                expiresAt: e.target.value ? new Date(e.target.value).toISOString() : undefined,
              })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional: When the notification should expire"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={createLoading || !newNotification.title || !newNotification.message}
          >
            {createLoading ? <CircularProgress size={24} /> : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Notification Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Notification</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {editError && (
              <Alert severity="error" onClose={() => setEditError(null)}>
                {editError}
              </Alert>
            )}

            <Alert severity="info">
              Note: Editing a notification updates it for all users who haven't read it yet.
            </Alert>

            <TextField
              label="Title"
              value={editInput.title || ''}
              onChange={(e) => setEditInput({ ...editInput, title: e.target.value })}
              fullWidth
            />

            <TextField
              label="Message"
              value={editInput.message || ''}
              onChange={(e) => setEditInput({ ...editInput, message: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />

            <TextField
              label="Action URL"
              value={editInput.actionUrl || ''}
              onChange={(e) => setEditInput({ ...editInput, actionUrl: e.target.value || undefined })}
              fullWidth
            />

            <TextField
              label="Action Button Label"
              value={editInput.actionLabel || ''}
              onChange={(e) => setEditInput({ ...editInput, actionLabel: e.target.value || undefined })}
              fullWidth
            />

            <TextField
              label="Expires At"
              type="datetime-local"
              value={editInput.expiresAt ? editInput.expiresAt.slice(0, 16) : ''}
              onChange={(e) => setEditInput({
                ...editInput,
                expiresAt: e.target.value ? new Date(e.target.value).toISOString() : undefined,
              })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{notificationToDelete?.title}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. Users who haven't read this notification will no longer see it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
